import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountApi } from '../api/account';
import { useAuth } from '../context/AuthContext';
import { useToast } from './Toast';
import type { ProductListItemDto } from '../api/types';
import { money } from '../lib/format';

/** Urun karti: liste, oneri ve favori ekranlarinda ortak kullanilir. */
export function ProductCard({ product }: { product: ProductListItemDto }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: wishlistIds = [] } = useQuery({
    queryKey: ['account', 'wishlist-ids'],
    queryFn: () => accountApi.wishlistIds(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  const isFavorited = wishlistIds.includes(product.id);

  const toggleMutation = useMutation({
    mutationFn: () => accountApi.toggleWishlist(product.id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist-ids'] });
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist'] });
      toast.success(result.message);
    },
    onError: (err) => {
      toast.fromError(err, 'Favori işlemi gerçekleştirilemedi.');
    },
  });

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.info('Favorilere eklemek için lütfen giriş yapın.');
      navigate(`/giris?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    toggleMutation.mutate();
  };

  const hasRange = product.maxPrice > product.minPrice;
  const discounted =
    product.compareAtPrice !== null &&
    product.compareAtPrice !== undefined &&
    product.compareAtPrice > product.minPrice;

  const hasPriceDrop =
    product.priceWhenAdded !== null &&
    product.priceWhenAdded !== undefined &&
    product.priceWhenAdded > product.minPrice;

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-ink-200 bg-white transition hover:shadow-card">
      <button
        type="button"
        onClick={handleFavoriteClick}
        disabled={toggleMutation.isPending}
        className={`absolute right-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur transition hover:scale-110 hover:bg-white active:scale-95 ${
          isFavorited ? 'text-rose-500' : 'text-ink-400 hover:text-rose-500'
        }`}
        aria-label={isFavorited ? 'Favorilerden çıkar' : 'Favorilere ekle'}
        title={isFavorited ? 'Favorilerden çıkar' : 'Favorilere ekle'}
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4 transition-colors"
          fill={isFavorited ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <Link to={`/urun/${product.slug}`} className="relative block aspect-square overflow-hidden bg-ink-50">
        {product.primaryImageUrl ? (
          /\.(mp4|webm|mov)(\?.*)?$/i.test(product.primaryImageUrl) ? (
            <video
              src={product.primaryImageUrl}
              muted
              loop
              playsInline
              preload="metadata"
              className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            />
          ) : (
            <img
              src={product.primaryImageUrl}
              alt={product.title}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            />
          )
        ) : (
          <div className="flex h-full items-center justify-center text-ink-300">
            <svg viewBox="0 0 24 24" className="h-10 w-10" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M3 15l4.5-4.5 4 4 3-3L21 16" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}

        <div className="absolute left-2 top-2 flex flex-col gap-1">
          {hasPriceDrop ? (
            <span className="badge bg-emerald-600 font-semibold text-white shadow-sm">
              📉 Fiyatı Düştü!
            </span>
          ) : null}
          {product.isNewArrival ? (
            <span className="badge bg-brand-600 text-white">Yeni</span>
          ) : null}
          {product.discountPercentage ? (
            <span className="badge bg-red-600 text-white">%{Math.round(product.discountPercentage)}</span>
          ) : null}
          {product.isPreOrder ? (
            <span className="badge bg-amber-500 text-white">Ön sipariş</span>
          ) : null}
        </div>

        {!product.isInStock ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <span className="badge bg-ink-800 text-white">Tükendi</span>
          </div>
        ) : null}
      </Link>

      <div className="flex flex-1 flex-col p-4">
        {product.brandName ? (
          <p className="text-xs uppercase tracking-wide text-ink-400">{product.brandName}</p>
        ) : null}

        <h3 className="mt-1 line-clamp-2 text-sm font-medium text-ink-900">
          <Link to={`/urun/${product.slug}`} className="hover:text-brand-700">
            {product.title}
          </Link>
        </h3>

        {product.reviewCount > 0 ? (
          <div className="mt-1.5 flex items-center gap-1 text-xs text-ink-500">
            <Stars value={product.averageRating} />
            <span>({product.reviewCount})</span>
          </div>
        ) : null}

        <div className="mt-auto pt-3">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-base font-semibold text-ink-900">
              {money(product.minPrice, product.currencyCode)}
              {hasRange ? <span className="text-sm font-normal text-ink-500"> – {money(product.maxPrice, product.currencyCode)}</span> : null}
            </span>
            {discounted ? (
              <span className="text-sm text-ink-400 line-through">
                {money(product.compareAtPrice, product.currencyCode)}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

export function Stars({ value, className = 'h-3.5 w-3.5' }: { value: number; className?: string }) {
  return (
    <span className="flex items-center" aria-label={`${value.toFixed(1)} / 5 puan`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          viewBox="0 0 20 20"
          className={`${className} ${star <= Math.round(value) ? 'text-amber-400' : 'text-ink-200'}`}
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M10 1.8l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 14.1l-4.8 2.6.9-5.4L2.2 7.5l5.4-.8L10 1.8z" />
        </svg>
      ))}
    </span>
  );
}

export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="overflow-hidden rounded-xl border border-ink-200">
          <div className="skeleton aspect-square rounded-none" />
          <div className="space-y-2 p-4">
            <div className="skeleton h-3 w-1/3" />
            <div className="skeleton h-4 w-full" />
            <div className="skeleton h-4 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
